import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Gift, Sparkles, Package, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { useCart, DISCOUNT_CODES } from '../../context/CartContext';
import { toast } from 'sonner';
import { getOptimizedImageUrl } from '../../utils/image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMBO_CODE = 'COMBO10';
const COMBO_PERCENT = DISCOUNT_CODES[COMBO_CODE]?.percent || 10;

// Curated combo suggestions
export const COMBO_THEMES = [
  {
    id: 'wealth',
    title: 'Wealth & Prosperity Combo',
    description: 'Attract financial abundance',
    icon: '💰',
    color: 'from-yellow-400 to-amber-500',
    categories: ['bracelets', 'yantras', 'crystal-trees'],
  },
  {
    id: 'health',
    title: 'Health & Healing Combo',
    description: 'Boost your physical well-being',
    icon: '💚',
    color: 'from-green-400 to-emerald-500',
    categories: ['bracelets', 'rudraksha', 'gemstones'],
  },
  {
    id: 'protection',
    title: 'Protection & Safety Combo',
    description: 'Shield from negative energies',
    icon: '🛡️',
    color: 'from-blue-400 to-indigo-500',
    categories: ['rudraksha', 'yantras', 'bracelets'],
  },
  {
    id: 'love',
    title: 'Love & Relationship Combo',
    description: 'Enhance love in your life',
    icon: '❤️',
    color: 'from-pink-400 to-rose-500',
    categories: ['gemstones', 'bracelets', 'crystal-trees'],
  },
];

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(price);

export const CombosSection = () => {
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openCombo, setOpenCombo] = useState(null);
  const { addToCart, applyDiscountCode } = useCart();

  useEffect(() => {
    const allCats = [...new Set(COMBO_THEMES.flatMap((t) => t.categories))];
    Promise.all(
      allCats.map((c) =>
        fetch(`${API_URL}/api/products?category=${c}&limit=10&lite=true`)
          .then((r) => r.json())
          .then((d) => [c, Array.isArray(d) ? d : []])
          .catch(() => [c, []])
      )
    )
      .then((entries) => setCategoryProducts(Object.fromEntries(entries)))
      .catch((e) => console.error('Error fetching combo products:', e))
      .finally(() => setLoading(false));
  }, []);

  // Deterministic: pick one unique product from each category of a combo
  const comboProductsMap = useMemo(() => {
    const map = {};
    COMBO_THEMES.forEach((theme) => {
      const picked = [];
      const seen = new Set();
      theme.categories.forEach((cat) => {
        const list = categoryProducts[cat] || [];
        const product = list.find((p) => !seen.has(p.id));
        if (product) {
          picked.push(product);
          seen.add(product.id);
        }
      });
      map[theme.id] = picked;
    });
    return map;
  }, [categoryProducts]);

  const handleAddCombo = (comboProducts) => {
    comboProducts.forEach((p) => p && addToCart(p));
    applyDiscountCode(COMBO_CODE);
    toast.success(`${comboProducts.length} items added! Extra ${COMBO_PERCENT}% off (${COMBO_CODE}) at checkout.`);
    setOpenCombo(null);
  };

  const activeTheme = COMBO_THEMES.find((t) => t.id === openCombo);
  const activeProducts = openCombo ? comboProductsMap[openCombo] || [] : [];
  const activeTotal = activeProducts.reduce((s, p) => s + (p?.price || 0), 0);
  const activeBundleDiscount = Math.round((activeTotal * COMBO_PERCENT) / 100);
  const activeFinal = activeTotal - activeBundleDiscount;

  if (loading) {
    return (
      <section className="py-12 lg:py-16 bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-white/20 rounded w-48 mb-8 mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/10 rounded-2xl p-6 h-64"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-12 lg:py-20 bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 relative overflow-hidden"
      data-testid="combos-section"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500 rounded-full blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full mb-4 border border-white/20">
            <Gift className="w-5 h-5" />
            <span className="font-bold text-sm">SPECIAL COMBOS</span>
            <Sparkles className="w-5 h-5" />
          </div>
          <h2 className="font-serif text-3xl lg:text-5xl font-bold text-white mb-4">
            Curated{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
              Combo Deals
            </span>
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Hand-picked product combinations for maximum spiritual benefits. Save more when you buy together!
          </p>
        </motion.div>

        {/* Combo Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COMBO_THEMES.map((theme, index) => {
            const comboProducts = comboProductsMap[theme.id] || [];
            const totalPrice = comboProducts.reduce((sum, p) => sum + (p?.price || 0), 0);
            const originalPrice = comboProducts.reduce((sum, p) => sum + (p?.originalPrice || p?.price || 0), 0);
            const savings = originalPrice - totalPrice;

            return (
              <motion.div
                key={theme.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/15 transition-all h-full flex flex-col">
                  {/* Header */}
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${theme.color} flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform shadow-lg`}
                  >
                    {theme.icon}
                  </div>

                  <h3 className="font-serif text-xl font-bold text-white mb-1">{theme.title}</h3>
                  <p className="text-white/60 text-sm mb-4">{theme.description}</p>

                  {/* Mini Product Preview */}
                  <div className="flex -space-x-3 mb-4">
                    {comboProducts.slice(0, 3).map((product, i) => (
                      <div
                        key={i}
                        className="w-12 h-12 rounded-full border-2 border-white/30 overflow-hidden bg-white"
                        title={product?.name}
                      >
                        {product?.image && (
                          <img src={getOptimizedImageUrl(product.image, '100x100')} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        )}
                      </div>
                    ))}
                    {comboProducts.length > 0 && (
                      <div className="w-12 h-12 rounded-full border-2 border-white/30 bg-white/20 flex items-center justify-center text-white text-xs font-bold">
                        {comboProducts.length} items
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="mt-auto">
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-2xl font-bold text-white">{formatPrice(totalPrice)}</span>
                      {savings > 0 && (
                        <span className="text-sm text-green-400 font-medium">Save {formatPrice(savings)}</span>
                      )}
                    </div>

                    {/* View Details opens combo dialog */}
                    <Button
                      onClick={() => setOpenCombo(theme.id)}
                      className={`w-full bg-gradient-to-r ${theme.color} text-white hover:opacity-90 font-semibold`}
                      disabled={comboProducts.length === 0}
                      data-testid={`combo-view-${theme.id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Combo Details
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/products">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-purple-900 px-8"
            >
              Create Your Own Combo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Combo Details Dialog */}
      <Dialog open={!!openCombo} onOpenChange={(o) => !o && setOpenCombo(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="combo-dialog">
          {activeTheme && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${activeTheme.color} flex items-center justify-center text-2xl shadow`}
                  >
                    {activeTheme.icon}
                  </div>
                  <div>
                    <DialogTitle className="font-serif text-xl">{activeTheme.title}</DialogTitle>
                    <DialogDescription>{activeTheme.description}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Products in this combo */}
              <div className="space-y-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  This combo includes {activeProducts.length} items:
                </p>
                {activeProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    onClick={() => setOpenCombo(null)}
                    className="flex items-center gap-3 p-2 rounded-lg border border-amber-100 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                    data-testid={`combo-item-${product.id}`}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-amber-50 flex-shrink-0">
                      {product.image && (
                        <img src={getOptimizedImageUrl(product.image, '100x100')} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground line-clamp-2">{product.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-bold text-gold">{formatPrice(product.price)}</span>
                        {product.originalPrice > product.price && (
                          <span className="text-xs text-muted-foreground line-through">
                            {formatPrice(product.originalPrice)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-amber-100 pt-3 space-y-1">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Subtotal</span>
                  <span>{formatPrice(activeTotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-green-600">
                  <span className="font-medium">Bundle Offer ({COMBO_CODE}) · {COMBO_PERCENT}% off</span>
                  <span className="font-semibold">- {formatPrice(activeBundleDiscount)}</span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-semibold text-foreground">You Pay</span>
                  <span className="text-xl font-bold text-foreground">{formatPrice(activeFinal)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground text-right">
                  {COMBO_PERCENT}% discount auto-applied at checkout
                </p>
              </div>

              <Button
                onClick={() => handleAddCombo(activeProducts)}
                className={`w-full bg-gradient-to-r ${activeTheme.color} text-white hover:opacity-90 font-semibold`}
                disabled={activeProducts.length === 0}
                data-testid="combo-add-to-cart"
              >
                <Package className="w-4 h-4 mr-2" />
                Add Combo to Cart
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
